/** @format */

import * as RadixAccordion from "@radix-ui/react-accordion";
import { a, useSpring } from "@react-spring/web";
import React, { memo, type FC, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { fixFalslyTranslatedMarkdown } from "senf-shared";
import { useMeasure } from "../../../hooks/useMeasure";
import { cn } from "../../../util/classNameConstructor";
import Icon from "../../core/icon/Icon";
import Markdown from "../../core/richTextEditor/Markdown";
import type { AccordionData, AccordionProps } from "./Accordion.types";

const highlightTextOnSearch = (text: string, term?: string) => {
  if (!term) return text;
  const parts = text.split(new RegExp(`(${term})`, "gi"));

  const result = parts.map((part: string, i: number) =>
    part.toLowerCase() === term.toLowerCase() ? (
      <mark key={i} className="rounded bg-yellow-200">
        {part}
      </mark>
    ) : (
      part
    ),
  );

  return result;
};

export const AccordionItem: FC<{
  stackTitle: string;
  stackContent: string | string[];
  searchTerm?: string;
  defaultOpen?: boolean;
}> = memo(({ stackTitle, stackContent, searchTerm, defaultOpen = false }) => {
  const [isOpen, setOpen] = useState(defaultOpen);
  const [bind, { height: viewHeight }] = useMeasure<HTMLDivElement>();
  const paddingBottom = 20;
  const style = useSpring({
    from: { height: 0, opacity: 0, y: 0 },
    to: {
      height: isOpen ? viewHeight + paddingBottom : 0,
      opacity: isOpen ? 1 : 0,
      scale: isOpen ? 1 : 0.97,
      y: isOpen ? 0 : -20,
    },
    config: {
      tension: 700,
      friction: 50,
    },
    delay: (prop) =>
      ({
        height: isOpen ? 0 : 50,
        opacity: isOpen ? 100 : 0,
        y: isOpen ? 50 : 0,
        scale: isOpen ? 50 : 0,
      })[prop] || 0,
  });

  useEffect(() => {
    setOpen(!!searchTerm);
  }, [searchTerm]);

  return (
    <RadixAccordion.Item
      value={stackTitle}
      className={`accordion-item${isOpen ? " is-open" : ""}`}
    >
      <RadixAccordion.Header className="focusable -mx-3">
        <RadixAccordion.Trigger
          className="focusable hover:bg-surface-subdued group relative z-10 flex w-full cursor-pointer items-center justify-between rounded-xl p-3"
          onClick={() => setOpen(!isOpen)}
        >
          <span className="body-md-bold text-start">
            {highlightTextOnSearch(stackTitle, searchTerm)}
          </span>
          <Icon
            name="arrowhead-down-md"
            className={cn("shrink-0", { "rotate-180": isOpen })}
          />
        </RadixAccordion.Trigger>
      </RadixAccordion.Header>
      <a.div style={style}>
        <RadixAccordion.Content
          {...bind}
          className="focusable overflow-hidden transition-all duration-300 ease-in-out"
        >
          <div className={cn("body-md pb-3 pr-3 leading-relaxed")}>
            {Array.isArray(stackContent) ? (
              <div>
                {stackContent.map((item) => (
                  <div
                    key={item.toString()}
                    className={item[0] === "-" ? "ml-12" : "my-2"}
                  >
                    {highlightTextOnSearch(
                      item[0] === "-" ? item.slice(1) : item,
                      searchTerm,
                    )}
                  </div>
                ))}
              </div>
            ) : (
              <Markdown>{fixFalslyTranslatedMarkdown(stackContent)}</Markdown>
            )}
          </div>
        </RadixAccordion.Content>
      </a.div>
    </RadixAccordion.Item>
  );
});

AccordionItem.displayName = "AccordionItem";

const AccordionCategory: FC<{
  categoryTitle: string;
  items: AccordionData[];
  searchTerm?: string;
}> = ({ categoryTitle, items, searchTerm }) => {
  return (
    <div className="flex flex-col">
      {categoryTitle !== "" && (
        <span className="body-md text-text-subdued focusable mt-5">
          {highlightTextOnSearch(categoryTitle, searchTerm)}
        </span>
      )}

      <RadixAccordion.Root type="multiple">
        {items.map(({ stackTitle, stackContent }) => (
          <AccordionItem
            key={stackTitle}
            stackTitle={stackTitle}
            stackContent={stackContent}
            searchTerm={searchTerm}
          />
        ))}
      </RadixAccordion.Root>
    </div>
  );
};

const Accordion: FC<AccordionProps> = ({ data, searchTerm }) => {
  const { t } = useTranslation();
  const groupedDataByCategory = data.reduce(
    (grouped, item) => {
      const categoryItem = { ...item, category: item.category || "" };
      const { category } = categoryItem;
      if (!grouped[category]) {
        grouped[category] = [];
      }
      grouped[category].push(categoryItem);
      return grouped;
    },
    {} as Record<string, typeof data>,
  );

  const filteredDataBySearchTerm = Object.entries(groupedDataByCategory).reduce(
    (filtered, [category, items]) => {
      if (!searchTerm) return groupedDataByCategory;

      const filteredItems = items.filter(
        (item) =>
          item.stackTitle.toLowerCase().includes(searchTerm.toLowerCase()) ||
          item.stackContent
            .toString()
            .toLowerCase()
            .includes(searchTerm.toLowerCase()),
      );
      if (filteredItems.length) {
        filtered[category] = filteredItems;
      }

      return filtered;
    },
    {} as Record<string, typeof data>,
  );

  return (
    <div>
      {Object.keys(filteredDataBySearchTerm).length === 0 && (
        <span className="flex w-full items-center justify-center">
          {t("global:noSearchResults")}
        </span>
      )}
      {Object.entries(filteredDataBySearchTerm).map(
        ([category, items], index) => (
          <AccordionCategory
            key={`Accordion-category-key-${category}`}
            categoryTitle={category}
            items={items}
            searchTerm={searchTerm}
          />
        ),
      )}
    </div>
  );
};

export default Accordion;
